"use client";

import Link from "next/link";
import type { ReactNode } from "react";

export function BrandMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <defs>
        <linearGradient id="auth-bm" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="var(--blue-950)" />
          <stop offset="1" stopColor="var(--blue-600)" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="14" fill="url(#auth-bm)" />
      <rect x="18" y="14" width="8" height="36" rx="3" fill="#fff" />
      <rect x="18" y="14" width="28" height="8" rx="3" fill="#fff" />
      <rect x="18" y="28" width="20" height="7" rx="3" fill="rgba(255,255,255,.6)" />
      <circle cx="46" cy="46" r="5" fill="var(--blue-500)" />
    </svg>
  );
}

interface AuthShellProps {
  children: ReactNode;
  panelTitle: ReactNode;
  panelSubtitle: string;
  panelHighlights?: { stat: string; label: string }[];
  // On the seaaii.com front-end, swap the FlowCloser brand mark/wordmark for
  // the Seaaii wordmark. Same shared auth app underneath.
  isSeaaii?: boolean;
}

const DEFAULT_HIGHLIGHTS = [
  { stat: "2,400+", label: "ร้านค้าใช้งาน" },
  { stat: "92%", label: "อัตราตอบลูกค้า" },
  { stat: "3.2x", label: "ยอดขายเฉลี่ย" },
];

/**
 * Split-screen auth layout:
 *   - Left: navy panel with FlowCloser branding (lg+ only)
 *   - Right: form area (always visible)
 */
export function AuthShell({ children, panelTitle, panelSubtitle, panelHighlights, isSeaaii = false }: AuthShellProps) {
  const highlights = panelHighlights ?? DEFAULT_HIGHLIGHTS;

  return (
    <main
      className="min-h-screen grid lg:grid-cols-2"
      style={{
        background: "var(--bg-soft)",
        fontFamily: "'Noto Sans Thai', system-ui, sans-serif",
        color: "var(--ink)",
      }}
    >
      {/* Left: navy brand panel — desktop only */}
      <aside
        className="hidden lg:flex relative overflow-hidden flex-col justify-between p-12"
        style={{
          color: "#fff",
          background:
            "radial-gradient(900px 500px at 80% -10%, rgba(96,165,250,.45), transparent 60%)," +
            "radial-gradient(800px 600px at -10% 110%, rgba(11,30,79,.6), transparent 70%)," +
            "linear-gradient(135deg, var(--blue-950) 0%, var(--blue-900) 50%, var(--blue-700) 100%)",
        }}
      >
        {/* grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.18,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%,#000 30%,transparent 80%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 40%,#000 30%,transparent 80%)",
          }}
        />

        <div className="relative">
          <Link href="/" className="inline-flex items-center gap-3">
            {isSeaaii ? (
              <span className="font-bold text-[22px] tracking-tight" style={{ color: "#fff" }}>
                Seaaii
              </span>
            ) : (
              <>
                <BrandMark size={32} />
                <span className="font-bold text-[20px] tracking-tight">
                  <span style={{ color: "#fff" }}>Flow</span>
                  <span
                    className="font-semibold"
                    style={{
                      background: "linear-gradient(90deg, #bfdbfe, #fff)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Closer
                  </span>
                </span>
              </>
            )}
          </Link>
        </div>

        <div className="relative">
          <h2 className="text-[36px] font-bold leading-tight tracking-tight mb-5" style={{ textWrap: "balance" }}>
            {panelTitle}
          </h2>
          <p className="text-[15px] leading-relaxed max-w-md" style={{ color: "#cfdcf5" }}>
            {panelSubtitle}
          </p>

          <div className="grid grid-cols-3 gap-6 mt-10 max-w-md">
            {highlights.map((h) => (
              <div key={h.label}>
                <div
                  className="text-[22px] font-bold leading-none"
                  style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-0.02em" }}
                >
                  {h.stat}
                </div>
                <div className="text-xs mt-1.5" style={{ color: "#a9bbe0" }}>
                  {h.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Keep the slot (panel uses justify-between) but drop the Formac
            attribution on the Seaaii front-end. */}
        <div className="relative text-xs" style={{ color: "#a9bbe0" }}>
          {!isSeaaii && <>© {new Date().getFullYear()} Super Firm Co., Ltd. · made for Thai SMEs</>}
        </div>
      </aside>

      {/* Right: form area */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div
          className="w-full max-w-md rounded-2xl"
          style={{
            background: "#fff",
            border: "1px solid var(--line)",
            boxShadow: "0 24px 60px -20px rgba(11,20,55,.12), 0 0 0 1px var(--line)",
            padding: "36px 32px",
          }}
        >
          {children}
        </div>
      </div>
    </main>
  );
}
